import { API_HOST, STORAGE_KEY } from "@/lib/config"

const TRPC_BASE = `${API_HOST}/trpc`

async function tRPCRequest<T>(
  procedure: string,
  input?: unknown,
  method?: "GET" | "POST",
): Promise<T> {
  const token = localStorage.getItem(STORAGE_KEY)
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  let url = `${TRPC_BASE}/${procedure}`
  const fetchOptions: RequestInit = { headers }

  if (method === "GET" || (method === undefined && input === undefined)) {
    fetchOptions.method = "GET"
    if (input !== undefined) {
      url = `${url}?input=${encodeURIComponent(JSON.stringify(input))}`
    }
  } else {
    fetchOptions.method = "POST"
    if (input !== undefined) {
      fetchOptions.body = JSON.stringify(input)
    }
  }

  const response = await fetch(url, fetchOptions)
  const data = await response.json()

  if (!response.ok) {
    const errorMessage = data?.error?.message || "请求失败"
    throw new Error(errorMessage)
  }

  return data.result.data as T
}

// ─── Types ──────────────────────────────────────────────────────

export interface PrepTemplateItem {
  code: string
  examName: string
  competitionCode: string
  subject: string
  grade: string
  stage: string
  defaultTotalWeeks: number
  dailyMinutes: number
  moduleCount: number
}

export interface WeeklyPlanItem {
  week: number
  theme: string
  knowledgePoints: string[]
  goals: string[]
  dailyMinutes: number
  status: "upcoming" | "in_progress" | "completed"
}

export interface PrepPlanListItem {
  id: string
  exam_name: string
  competition_code: string
  competition_year: number
  exam_date: string
  subject: string
  status: string
  template_code: string
  total_weeks: number
  start_date: string
  end_date: string
  current_week: number
  completed_days: number
  created_at: string
}

export interface PrepPlanDetail extends PrepPlanListItem {
  weekly_plan: WeeklyPlanItem[]
  diagnosis_id: string
  course_id: string
  total_practice_count: number
  correct_count: number
  push_enabled: boolean
}

export interface PrepDailyTaskItem {
  type: "lesson" | "practice" | "retry" | "review"
  title: string
  refId: string
  duration: number
  status: "pending" | "done" | "skipped"
  prepPlanId?: string
}

// ─── API Functions ──────────────────────────────────────────────

export async function getPrepTemplates(): Promise<PrepTemplateItem[]> {
  return tRPCRequest("examPrep.getTemplates", undefined, "GET")
}

export async function createPrepPlan(input: {
  templateCode: string
  examDate?: string
  dailyMinutes?: number
  diagnosisId?: string
  competitionYear?: number
}): Promise<PrepPlanDetail> {
  return tRPCRequest("examPrep.create", input)
}

export async function listPrepPlans(): Promise<PrepPlanListItem[]> {
  return tRPCRequest("examPrep.list", undefined, "GET")
}

export async function getPrepPlan(planId: string): Promise<PrepPlanDetail> {
  return tRPCRequest("examPrep.get", { planId }, "GET")
}

export async function generatePrepDailyTasks(
  planId: string,
  date?: string,
): Promise<{ date: string; tasks: PrepDailyTaskItem[] }> {
  return tRPCRequest("examPrep.generateDailyTasks", { planId, date })
}

export async function updatePrepProgress(
  planId: string,
  input: {
    taskCompleted: boolean
    correctCount?: number
    totalAttempted?: number
  },
): Promise<void> {
  return tRPCRequest("examPrep.updateProgress", { planId, ...input })
}

export async function pausePrepPlan(planId: string): Promise<void> {
  return tRPCRequest("examPrep.pause", { planId })
}

export async function resumePrepPlan(planId: string): Promise<void> {
  return tRPCRequest("examPrep.resume", { planId })
}

export async function abandonPrepPlan(planId: string): Promise<void> {
  return tRPCRequest("examPrep.abandon", { planId })
}
